export default function components(theme) {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
          margin: 0,
          padding: 0,
          backgroundColor: theme.palette.background.default,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          transition: 'background-color 0.3s ease',
        },
        '#root': {
          height: '100%',
        },
        // Scrollbar customization
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          background: theme.palette.grey[300],
          borderRadius: '4px',
          '&:hover': {
            background: theme.palette.grey[400],
          },
        },
        // Animations
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '.fade-in': {
          animation: 'fadeIn 0.5s ease-out forwards',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Smooth rounding
          backgroundImage: 'none',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0px 10px 30px rgba(0, 0, 0, 0.3)' // Deeper shadow for dark mode
              : '0px 10px 40px -10px rgba(0, 0, 0, 0.08)', // Soft, large shadow for light mode
          backgroundColor: theme.palette.background.paper,
          border: 'none',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)', // Tactile lift
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0px 15px 35px rgba(0, 0, 0, 0.4)'
                : '0px 20px 40px -10px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '24px 32px 16px', // Increased padding
        },
        title: {
          fontSize: '1.25rem',
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px 32px', // Increased padding
          '&:last-child': {
            paddingBottom: '32px',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 10px 30px rgba(0,0,0,0.1)',
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 10px 30px rgba(0,0,0,0.1)',
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.success.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 22,
          height: 22,
        },
        track: {
          borderRadius: 26 / 2,
          backgroundColor: theme.palette.error.main,
          opacity: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: 500,
          }),
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          height: '46px', // Taller buttons
          padding: '8px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
        },
        containedPrimary: {
          background: theme.palette.primary.main,
          color: '#fff',
          '&:hover': {
            background: theme.palette.primary.dark,
            boxShadow: `0 8px 16px -4px ${theme.palette.primary.light}`,
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          background: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          '&:hover': {
            background: theme.palette.secondary.dark,
            boxShadow: `0 8px 16px -4px ${theme.palette.secondary.light}`,
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          transition: 'all 0.2s',
          backgroundColor: theme.palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[300],
            borderWidth: '1px',
          },
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#FFFDF5', // Warm hover
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
            boxShadow: `0 0 0 4px ${theme.palette.primary.light}40`, // focus ring
          },
        },
        input: {
          padding: '14px 16px',
          height: 'auto',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          // Minimal header, slight orange tint or clean white
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.background.paper : '#FFF3E0',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '18px 24px', // Relaxed density
          fontSize: '0.9rem',
        },
        head: {
          fontWeight: 700,
          color: theme.palette.primary.dark,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.75rem',
          backgroundColor: 'transparent',
        },
        body: {
          color: theme.palette.text.primary,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            // Soft warm yellow hover
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(245, 124, 0, 0.08) !important'
                : '#FFFDE7 !important',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 600,
          fontSize: '0.95rem',
          alignItems: 'center',
          padding: '16px 24px',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
          animation: 'fadeIn 0.5s ease-out forwards', // Animation
          marginBottom: '16px',
          position: 'relative',
          overflow: 'hidden',
        },
        message: {
          padding: 0,
          width: '100%',
        },
        icon: {
          opacity: 1,
          padding: 0,
          marginRight: '16px',
          fontSize: '24px',
        },
        standardSuccess: {
          background: `linear-gradient(135deg, ${theme.palette.success.light} 0%, #FFFFFF 100%)`, // Gradient
          color: theme.palette.success.dark,
          border: `1px solid ${theme.palette.success.light}`,
          borderLeft: `6px solid ${theme.palette.success.main}`, // Accent border
          '& .MuiAlert-icon': {
            color: theme.palette.success.main,
          },
        },
        standardError: {
          background: `linear-gradient(135deg, ${theme.palette.error.light} 0%, #FFFFFF 100%)`,
          color: theme.palette.error.dark,
          border: `1px solid ${theme.palette.error.light}`,
          borderLeft: `6px solid ${theme.palette.error.main}`,
          '& .MuiAlert-icon': {
            color: theme.palette.error.main,
          },
        },
        standardWarning: {
          background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, #FFFFFF 100%)`,
          color: theme.palette.warning.dark,
          border: `1px solid ${theme.palette.warning.light}`,
          borderLeft: `6px solid ${theme.palette.warning.main}`,
          '& .MuiAlert-icon': {
            color: theme.palette.warning.main,
          },
        },
        standardInfo: {
          background: `linear-gradient(135deg, ${theme.palette.info.light} 0%, #FFFFFF 100%)`,
          color: theme.palette.info.dark,
          border: `1px solid ${theme.palette.info.light}`,
          borderLeft: `6px solid ${theme.palette.info.main}`,
          '& .MuiAlert-icon': {
            color: theme.palette.info.main,
          },
        },
        filledSuccess: {
          backgroundColor: theme.palette.success.main,
          color: '#fff',
          boxShadow: '0 8px 16px rgba(46, 125, 50, 0.24)',
          '& .MuiAlert-icon': {
            color: '#fff',
          },
        },
        filledError: {
          backgroundColor: theme.palette.error.main,
          color: '#fff',
          boxShadow: '0 8px 16px rgba(211, 47, 47, 0.24)',
          '& .MuiAlert-icon': {
            color: '#fff',
          },
        },
      },
    },
  };
}
